import { AnimatePresence, motion } from "framer-motion";
import { format, startOfToday } from "date-fns";
import {
  BedDouble,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  Sailboat,
  UtensilsCrossed,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { z } from "zod";
import { useLanguage } from "@/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

type ServiceType = "luxury-stays" | "yacht-charter" | "restaurant-reservations";
type FieldErrors = Record<string, string>;

interface EnquiryState {
  service: ServiceType | "";
  location: string;
  propertyType: string;
  checkIn: Date | undefined;
  checkOut: Date | undefined;
  budget: string;
  bedrooms: string;
  preferredArea: string;
  preferredAreaOther: string;
  charterDate: Date | undefined;
  guests: number;
  lunchSpot: string;
  lunchSpotOther: string;
  duration: string;
  customDuration: string;
  reservationDate: Date | undefined;
  reservationTime: string;
  restaurantPreference: string;
  preferredRestaurant: string;
  fullName: string;
  email: string;
  phone: string;
  additionalRequests: string;
}

const initialState: EnquiryState = {
  service: "",
  location: "",
  propertyType: "",
  checkIn: undefined,
  checkOut: undefined,
  budget: "",
  bedrooms: "",
  preferredArea: "",
  preferredAreaOther: "",
  charterDate: undefined,
  guests: 2,
  lunchSpot: "",
  lunchSpotOther: "",
  duration: "",
  customDuration: "",
  reservationDate: undefined,
  reservationTime: "",
  restaurantPreference: "recommendation",
  preferredRestaurant: "",
  fullName: "",
  email: "",
  phone: "",
  additionalRequests: "",
};

const destinations = ["Ibiza", "Marbella", "Saint-Tropez", "Paris", "Dubai", "Other"];

const areas: Record<string, string[]> = {
  Ibiza: ["Ibiza Town", "Marina Botafoch", "Talamanca", "Es Cubells", "Cala Jondal", "Santa Eulalia", "San José"],
  Marbella: ["Golden Mile", "Sierra Blanca", "Nueva Andalucía", "Puerto Banús", "La Zagaleta", "Benahavís"],
  "Saint-Tropez": ["Saint-Tropez Centre", "Ramatuelle", "Pampelonne", "Gassin", "Grimaud"],
  Paris: ["1st Arrondissement", "6th Arrondissement", "7th Arrondissement", "8th Arrondissement", "16th Arrondissement", "Le Marais"],
  Dubai: ["Palm Jumeirah", "Dubai Marina", "Downtown Dubai", "Jumeirah Bay", "Emirates Hills", "Jumeirah"],
  Other: [],
};

const lunchSpots: Record<string, string[]> = {
  Ibiza: ["Formentera", "Cala Jondal", "Es Torrent", "No preference / Surprise me", "Other"],
  Marbella: ["Puerto Banús", "Sotogrande", "Estepona", "No preference / Surprise me", "Other"],
  "Saint-Tropez": ["Pampelonne", "Port Grimaud", "Cannes", "No preference / Surprise me", "Other"],
  Paris: ["No preference / Surprise me", "Other"],
  Dubai: ["J1 Beach", "Palm Jumeirah", "Dubai Marina", "No preference / Surprise me", "Other"],
  Other: ["No preference / Surprise me", "Other"],
};

const budgetOptions: Record<ServiceType, string[]> = {
  "luxury-stays": ["€2,500 – €5,000", "€5,000 – €10,000", "€10,000 – €25,000", "€25,000+", "Flexible"],
  "yacht-charter": ["€1,500 – €3,000", "€3,000 – €6,000", "€6,000 – €12,000", "€12,000+", "Flexible"],
  "restaurant-reservations": [],
};

const inputClass = "h-12 rounded-none border-0 border-b border-primary-foreground/15 bg-transparent px-0 font-body text-base text-primary-foreground shadow-none placeholder:text-primary-foreground/25 focus-visible:border-bronze focus-visible:ring-0 focus-visible:ring-offset-0";
const labelClass = "font-label mb-3 block text-primary-foreground/45";
const selectTriggerClass = "h-12 rounded-none border-0 border-b border-primary-foreground/15 bg-transparent px-0 font-body text-base text-primary-foreground shadow-none focus:ring-0 focus:ring-offset-0 data-[placeholder]:text-primary-foreground/25";
const selectContentClass = "rounded-sm border-primary-foreground/10 bg-warm-black text-primary-foreground shadow-xl";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-2 font-body text-xs text-destructive" role="alert">{message}</p>;
}

function DateField({
  id,
  label,
  value,
  onChange,
  minDate,
  error,
}: {
  id: string;
  label: string;
  value?: Date;
  onChange: (date?: Date) => void;
  minDate?: Date;
  error?: string;
}) {
  const { t } = useLanguage();
  const earliest = minDate ?? startOfToday();

  return (
    <div>
      <Label htmlFor={id} className={labelClass}>{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="ghost"
            className={cn(inputClass, "w-full justify-between rounded-none px-0 font-normal hover:bg-transparent hover:text-primary-foreground")}
          >
            <span className={value ? "text-primary-foreground" : "text-primary-foreground/25"}>
              {value ? format(value, "dd MMMM yyyy") : t("Select date", "Selecteer datum")}
            </span>
            <CalendarDays className="h-4 w-4 text-bronze" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto rounded-sm border-primary-foreground/10 bg-warm-black p-0 text-primary-foreground shadow-xl">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            disabled={{ before: earliest }}
            initialFocus
            className="pointer-events-auto p-3"
          />
        </PopoverContent>
      </Popover>
      <FieldError message={error} />
    </div>
  );
}

function NumberSelector({ label, value, onChange, min = 1, max = 30 }: { label: string; value: number; onChange: (value: number) => void; min?: number; max?: number }) {
  return (
    <div>
      <Label className={labelClass}>{label}</Label>
      <div className="flex h-12 items-center justify-between border-b border-primary-foreground/15">
        <Button type="button" variant="ghost" size="icon" aria-label="Decrease" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} className="rounded-none text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground">
          <Minus className="h-4 w-4" />
        </Button>
        <span className="min-w-12 text-center font-display text-xl text-primary-foreground">{value}{value === max ? "+" : ""}</span>
        <Button type="button" variant="ghost" size="icon" aria-label="Increase" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} className="rounded-none text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

const stepTransition = { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const };

export default function ServiceEnquiryForm() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [values, setValues] = useState<EnquiryState>(initialState);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);
  const submitLock = useRef<boolean>(false);
  const submissionIdRef = useRef<string | null>(null);

  const update = <K extends keyof EnquiryState>(key: K, value: EnquiryState[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const updateLocation = (location: string) => {
    setValues((current) => ({
      ...current,
      location,
      preferredArea: "",
      preferredAreaOther: "",
      lunchSpot: "",
      lunchSpotOther: "",
    }));
    setErrors((current) => ({
      ...current,
      location: "",
      preferredArea: "",
      preferredAreaOther: "",
      lunchSpot: "",
      lunchSpotOther: "",
    }));
  };

  const serviceOptions = [
    { value: "luxury-stays" as const, label: t("Luxury Stays", "Luxe Verblijven"), icon: BedDouble },
    { value: "yacht-charter" as const, label: t("Yacht / Boat Charter", "Jacht / Boot Charter"), icon: Sailboat },
    { value: "restaurant-reservations" as const, label: t("Restaurant Reservations", "Restaurantreserveringen"), icon: UtensilsCrossed },
  ];

  const areaOptions = useMemo(() => [...(areas[values.location] ?? []), "No preference", "Other"], [values.location]);

  const validateCurrentStep = () => {
    if (step === 1) {
      if (!values.service) {
        setErrors({ service: t("Please select a service.", "Selecteer een service.") });
        return false;
      }
      return true;
    }

    if (step === 2) {
      const required = z.string().trim().min(1);
      const today = startOfToday();
      const nextErrors: FieldErrors = {};
      if (!required.safeParse(values.location).success) nextErrors.location = t("Select a destination.", "Selecteer een bestemming.");

      if (values.service === "luxury-stays") {
        if (!required.safeParse(values.propertyType).success) nextErrors.propertyType = t("Select a property type.", "Selecteer een woningtype.");
        if (!values.checkIn || values.checkIn < today) nextErrors.checkIn = t("Select a valid check-in date.", "Selecteer een geldige aankomstdatum.");
        if (!values.checkOut || values.checkOut < today) nextErrors.checkOut = t("Select a valid check-out date.", "Selecteer een geldige vertrekdatum.");
        if (values.checkIn && values.checkOut && values.checkOut <= values.checkIn) nextErrors.checkOut = t("Check-out must be after check-in.", "Vertrek moet na aankomst zijn.");
        if (!required.safeParse(values.budget).success) nextErrors.budget = t("Select a budget.", "Selecteer een budget.");
        if (!required.safeParse(values.bedrooms).success) nextErrors.bedrooms = t("Select the bedrooms.", "Selecteer het aantal slaapkamers.");
        if (!required.safeParse(values.preferredArea).success) nextErrors.preferredArea = t("Select a preferred area.", "Selecteer een voorkeursgebied.");
        if (values.preferredArea === "Other" && !required.max(100).safeParse(values.preferredAreaOther).success) nextErrors.preferredAreaOther = t("Enter your preferred area.", "Vul uw voorkeursgebied in.");
      }

      if (values.service === "yacht-charter") {
        if (!values.charterDate || values.charterDate < today) nextErrors.charterDate = t("Select a valid charter date.", "Selecteer een geldige charterdatum.");
        if (!required.safeParse(values.budget).success) nextErrors.budget = t("Select a budget.", "Selecteer een budget.");
        if (!required.safeParse(values.lunchSpot).success) nextErrors.lunchSpot = t("Select a lunch preference.", "Selecteer een lunchvoorkeur.");
        if (values.lunchSpot === "Other" && !required.max(100).safeParse(values.lunchSpotOther).success) nextErrors.lunchSpotOther = t("Enter your preferred lunch spot.", "Vul uw gewenste lunchplek in.");
        if (values.location !== "Ibiza" && !required.safeParse(values.duration).success) nextErrors.duration = t("Select a charter duration.", "Selecteer een charterduur.");
        if (values.duration === "Custom" && !required.max(100).safeParse(values.customDuration).success) nextErrors.customDuration = t("Enter your preferred duration.", "Vul uw gewenste duur in.");
      }

      if (values.service === "restaurant-reservations") {
        if (!values.reservationDate || values.reservationDate < today) nextErrors.reservationDate = t("Select a valid reservation date.", "Selecteer een geldige reserveringsdatum.");
        if (!required.safeParse(values.reservationTime).success) nextErrors.reservationTime = t("Select a preferred time.", "Selecteer een gewenste tijd.");
        if (values.restaurantPreference === "specific" && !required.max(100).safeParse(values.preferredRestaurant).success) nextErrors.preferredRestaurant = t("Enter a restaurant name.", "Vul een restaurantnaam in.");
      }

      setErrors(nextErrors);
      return Object.keys(nextErrors).length === 0;
    }

    const contactSchema = z.object({
      fullName: z.string().trim().min(2).max(100),
      email: z.string().trim().email().max(255),
      phone: z.string().trim().min(5).max(40),
      additionalRequests: z.string().trim().max(2000),
    });
    const result = contactSchema.safeParse(values);
    if (result.success) return true;
    const nextErrors: FieldErrors = {};
    result.error.issues.forEach((issue) => {
      const field = String(issue.path[0]);
      nextErrors[field] = field === "email"
        ? t("Enter a valid email address.", "Vul een geldig e-mailadres in.")
        : t("Please complete this field.", "Vul dit veld in.");
    });
    setErrors(nextErrors);
    return false;
  };

  const next = () => {
    if (!validateCurrentStep()) return;
    setErrors({});
    setStep((current) => Math.min(3, current + 1));
  };

  const submit = async () => {
    if (submitLock.current) return;
    if (!validateCurrentStep() || !values.service) return;
    submitLock.current = true;
    setLoading(true);

    if (!submissionIdRef.current) submissionIdRef.current = crypto.randomUUID();
    const submissionId = submissionIdRef.current;
    const ymd = (d: Date | undefined) => (d ? format(d, "yyyy-MM-dd") : null);
    const isStay = values.service === "luxury-stays";
    const isYacht = values.service === "yacht-charter";
    const isRestaurant = values.service === "restaurant-reservations";
    const serviceLabel = isStay ? "Luxury Stays" : isYacht ? "Yacht / Boat Charter" : "Restaurant Reservations";
    const charterDuration = !isYacht
      ? null
      : values.location === "Ibiza"
        ? "Full Day"
        : values.duration === "Custom"
          ? `Custom: ${values.customDuration.trim()}`
          : values.duration;

    const payload = {
      submission_id: submissionId,
      submitted_at: new Date().toISOString(),
      service_type: serviceLabel,
      full_name: values.fullName.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      location: values.location,
      additional_requests: values.additionalRequests.trim() || null,
      source: "Website",
      property_type: isStay ? values.propertyType : null,
      check_in_date: isStay ? ymd(values.checkIn) : null,
      check_out_date: isStay ? ymd(values.checkOut) : null,
      budget: isStay || isYacht ? values.budget : null,
      bedrooms: isStay ? parseInt(values.bedrooms, 10) : null,
      preferred_area: isStay ? (values.preferredArea === "Other" ? values.preferredAreaOther.trim() : values.preferredArea) : null,
      charter_date: isYacht ? ymd(values.charterDate) : null,
      number_of_guests: isYacht || isRestaurant ? values.guests : null,
      preferred_lunch_spot: isYacht ? (values.lunchSpot === "Other" ? values.lunchSpotOther.trim() : values.lunchSpot) : null,
      charter_duration: charterDuration,
      reservation_date: isRestaurant ? ymd(values.reservationDate) : null,
      preferred_time: isRestaurant ? values.reservationTime : null,
      restaurant_preference: isRestaurant ? (values.restaurantPreference === "recommendation" ? "recommendation" : "restaurant_in_mind") : null,
      restaurant_name: isRestaurant && values.restaurantPreference !== "recommendation" ? values.preferredRestaurant.trim() : null,
    };

    let errorCode: string | null = null;
    try {
      const { data, error } = await supabase.functions.invoke("submit-enquiry", { body: payload });
      if (error) {
        let body: { error?: string } | null = null;
        try { body = await (error as { context?: Response }).context?.json(); } catch { /* ignore */ }
        errorCode = body?.error ?? "invoke_failed";
      } else if (!data?.ok) {
        errorCode = data?.error ?? "unknown_error";
      }
    } catch {
      errorCode = "network_error";
    }

    setLoading(false);
    submitLock.current = false;
    if (errorCode) {
      console.error("submit-enquiry failed", { submission_id: submissionId, error: errorCode });
      toast({ title: t("We could not send your request. Please try again.", "We konden uw aanvraag niet verzenden. Probeer het opnieuw."), variant: "destructive" });
      return;
    }
    submissionIdRef.current = null;
    setStep(4);
  };

  const stepLabels = [t("Service", "Service"), t("Details", "Details"), t("Contact", "Contact"), t("Confirmation", "Bevestiging")];

  return (
    <div className="w-full">
      <div className="mb-12 flex items-center" aria-label={t("Enquiry progress", "Voortgang aanvraag")}>
        {stepLabels.map((label, index) => {
          const number = index + 1;
          const active = step >= number;
          return (
            <div key={label} className={cn("flex items-center", index < stepLabels.length - 1 && "flex-1")}>
              <div className={cn("flex items-center gap-2 whitespace-nowrap font-label transition-colors duration-700", active ? "text-bronze" : "text-primary-foreground/25")}>
                <span>{String(number).padStart(2, "0")}</span>
                <span className="hidden sm:inline">{label}</span>
              </div>
              {index < stepLabels.length - 1 && <span className={cn("mx-3 h-px flex-1 transition-colors duration-700 sm:mx-5", step > number ? "bg-bronze" : "bg-primary-foreground/10")} />}
            </div>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="service" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={stepTransition}>
            <span className="font-label text-bronze">{t("Select a service", "Kies een service")}</span>
            <h2 className="mt-4 font-display text-3xl leading-tight text-primary-foreground md:text-4xl">{t("How may we assist?", "Waarmee mogen wij u helpen?")}</h2>
            <div className="mt-9 grid gap-3">
              {serviceOptions.map(({ value, label, icon: Icon }) => {
                const selected = values.service === value;
                return (
                  <Button
                    key={value}
                    type="button"
                    variant="outline"
                    onClick={() => update("service", value)}
                    aria-pressed={selected}
                    className={cn("h-auto min-h-20 justify-start rounded-sm border-primary-foreground/10 bg-transparent px-5 py-4 text-left text-primary-foreground hover:border-bronze/60 hover:bg-primary-foreground/5 hover:text-primary-foreground", selected && "border-bronze bg-primary-foreground/5")}
                  >
                    <Icon className="mr-3 h-5 w-5 text-bronze" />
                    <span className="font-display text-lg font-light">{label}</span>
                    {selected && <Check className="ml-auto h-4 w-4 text-bronze" />}
                  </Button>
                );
              })}
            </div>
            <FieldError message={errors.service} />
            <div className="mt-10 flex justify-end">
              <Button type="button" onClick={next} className="h-12 rounded-sm bg-primary-foreground px-6 font-label text-warm-black hover:bg-primary-foreground/90">
                {t("Continue", "Verder")} <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}

        {step === 2 && values.service && (
          <motion.div key={`details-${values.service}`} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={stepTransition}>
            <span className="font-label text-bronze">{t("Your preferences", "Uw voorkeuren")}</span>
            <h2 className="mt-4 font-display text-3xl leading-tight text-primary-foreground md:text-4xl">
              {serviceOptions.find((option) => option.value === values.service)?.label}
            </h2>

            <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-9 md:grid-cols-2">
              <div>
                <Label className={labelClass}>{t("Location", "Locatie")}</Label>
                <Select value={values.location} onValueChange={updateLocation}>
                  <SelectTrigger className={selectTriggerClass}><SelectValue placeholder={t("Select destination", "Selecteer bestemming")} /></SelectTrigger>
                  <SelectContent className={selectContentClass}>{destinations.map((destination) => <SelectItem key={destination} value={destination}>{destination}</SelectItem>)}</SelectContent>
                </Select>
                <FieldError message={errors.location} />
              </div>

              {values.service === "luxury-stays" && (
                <>
                  <div>
                    <Label className={labelClass}>{t("Property type", "Woningtype")}</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[{ value: "Villa", label: t("Villa", "Villa") }, { value: "Apartment", label: t("Apartment", "Appartement") }].map(({ value: option, label }) => (
                        <Button key={option} type="button" variant="outline" onClick={() => update("propertyType", option)} className={cn("h-12 rounded-sm border-primary-foreground/10 bg-transparent font-body text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground", values.propertyType === option && "border-bronze bg-primary-foreground/5 text-primary-foreground")}>{label}</Button>
                      ))}
                    </div>
                    <FieldError message={errors.propertyType} />
                  </div>
                  <DateField id="check-in" label={t("Check-in date", "Aankomstdatum")} value={values.checkIn} onChange={(date) => { update("checkIn", date); if (date && values.checkOut && values.checkOut <= date) update("checkOut", undefined); }} error={errors.checkIn} />
                  <DateField id="check-out" label={t("Check-out date", "Vertrekdatum")} value={values.checkOut} onChange={(date) => update("checkOut", date)} minDate={values.checkIn ? new Date(values.checkIn.getFullYear(), values.checkIn.getMonth(), values.checkIn.getDate() + 1) : startOfToday()} error={errors.checkOut} />
                  <div>
                    <Label className={labelClass}>{t("Budget per stay", "Budget per verblijf")}</Label>
                    <Select value={values.budget} onValueChange={(value) => update("budget", value)}><SelectTrigger className={selectTriggerClass}><SelectValue placeholder={t("Select budget", "Selecteer budget")} /></SelectTrigger><SelectContent className={selectContentClass}>{budgetOptions[values.service].map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent></Select>
                    <FieldError message={errors.budget} />
                  </div>
                  <div>
                    <Label className={labelClass}>{t("Bedrooms", "Slaapkamers")}</Label>
                    <div className="grid grid-cols-6 gap-1">{["1", "2", "3", "4", "5", "6+"].map((option) => <Button key={option} type="button" variant="outline" onClick={() => update("bedrooms", option)} className={cn("h-12 min-w-0 rounded-sm border-primary-foreground/10 bg-transparent px-0 text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground", values.bedrooms === option && "border-bronze bg-primary-foreground/5 text-primary-foreground")}>{option}</Button>)}</div>
                    <FieldError message={errors.bedrooms} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className={labelClass}>{t("Preferred area", "Voorkeursgebied")}</Label>
                    <Select value={values.preferredArea} onValueChange={(value) => update("preferredArea", value)} disabled={!values.location}><SelectTrigger className={selectTriggerClass}><SelectValue placeholder={values.location ? t("Select area", "Selecteer gebied") : t("Select a location first", "Kies eerst een locatie")} /></SelectTrigger><SelectContent className={selectContentClass}>{areaOptions.map((option) => <SelectItem key={option} value={option}>{option === "No preference" ? t("No preference", "Geen voorkeur") : option === "Other" ? t("Other", "Anders") : option}</SelectItem>)}</SelectContent></Select>
                    <FieldError message={errors.preferredArea} />
                    {values.preferredArea === "Other" && <div className="mt-5"><Input value={values.preferredAreaOther} onChange={(event) => update("preferredAreaOther", event.target.value)} maxLength={100} placeholder={t("Preferred neighbourhood or area", "Gewenste buurt of omgeving")} className={inputClass} /><FieldError message={errors.preferredAreaOther} /></div>}
                  </div>
                </>
              )}

              {values.service === "yacht-charter" && (
                <>
                  <DateField id="charter-date" label={t("Charter date", "Charterdatum")} value={values.charterDate} onChange={(date) => update("charterDate", date)} error={errors.charterDate} />
                  <NumberSelector label={t("Number of guests", "Aantal gasten")} value={values.guests} onChange={(value) => update("guests", value)} />
                  <div>
                    <Label className={labelClass}>{t("Budget", "Budget")}</Label>
                    <Select value={values.budget} onValueChange={(value) => update("budget", value)}><SelectTrigger className={selectTriggerClass}><SelectValue placeholder={t("Select budget", "Selecteer budget")} /></SelectTrigger><SelectContent className={selectContentClass}>{budgetOptions[values.service].map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent></Select>
                    <FieldError message={errors.budget} />
                  </div>
                  <div>
                    <Label className={labelClass}>{t("Preferred lunch spot", "Gewenste lunchplek")}</Label>
                    <Select value={values.lunchSpot} onValueChange={(value) => update("lunchSpot", value)} disabled={!values.location}><SelectTrigger className={selectTriggerClass}><SelectValue placeholder={values.location ? t("Select preference", "Selecteer voorkeur") : t("Select a location first", "Kies eerst een locatie")} /></SelectTrigger><SelectContent className={selectContentClass}>{(lunchSpots[values.location] ?? []).map((option) => <SelectItem key={option} value={option}>{option === "No preference / Surprise me" ? t("No preference / Surprise me", "Geen voorkeur / Verras mij") : option === "Other" ? t("Other", "Anders") : option}</SelectItem>)}</SelectContent></Select>
                    <FieldError message={errors.lunchSpot} />
                    {values.lunchSpot === "Other" && <div className="mt-5"><Input value={values.lunchSpotOther} onChange={(event) => update("lunchSpotOther", event.target.value)} maxLength={100} placeholder={t("Preferred lunch spot", "Gewenste lunchplek")} className={inputClass} /><FieldError message={errors.lunchSpotOther} /></div>}
                  </div>
                  {values.location !== "Ibiza" && (
                    <div className="md:col-span-2">
                      <Label className={labelClass}>{t("Charter duration", "Charterduur")}</Label>
                      <Select value={values.duration} onValueChange={(value) => update("duration", value)}><SelectTrigger className={selectTriggerClass}><SelectValue placeholder={t("Select duration", "Selecteer duur")} /></SelectTrigger><SelectContent className={selectContentClass}>{["Half Day", "Full Day", "Multiple Days", "Custom"].map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent></Select>
                      <FieldError message={errors.duration} />
                      {values.duration === "Custom" && <div className="mt-5"><Input value={values.customDuration} onChange={(event) => update("customDuration", event.target.value)} maxLength={100} placeholder={t("Preferred duration or times", "Gewenste duur of tijden")} className={inputClass} /><FieldError message={errors.customDuration} /></div>}
                    </div>
                  )}
                </>
              )}

              {values.service === "restaurant-reservations" && (
                <>
                  <DateField id="reservation-date" label={t("Reservation date", "Reserveringsdatum")} value={values.reservationDate} onChange={(date) => update("reservationDate", date)} error={errors.reservationDate} />
                  <div>
                    <Label className={labelClass}>{t("Preferred time", "Gewenste tijd")}</Label>
                    <Select value={values.reservationTime} onValueChange={(value) => update("reservationTime", value)}><SelectTrigger className={selectTriggerClass}><SelectValue placeholder={t("Select time", "Selecteer tijd")} /></SelectTrigger><SelectContent className={selectContentClass}>{["12:00", "12:30", "13:00", "13:30", "14:00", "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30"].map((time) => <SelectItem key={time} value={time}>{time}</SelectItem>)}</SelectContent></Select>
                    <FieldError message={errors.reservationTime} />
                  </div>
                  <NumberSelector label={t("Number of guests", "Aantal gasten")} value={values.guests} onChange={(value) => update("guests", value)} />
                  <div>
                    <Label className={labelClass}>{t("Restaurant preference", "Restaurantvoorkeur")}</Label>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Button type="button" variant="outline" onClick={() => update("restaurantPreference", "recommendation")} className={cn("h-auto min-h-12 whitespace-normal rounded-sm border-primary-foreground/10 bg-transparent px-3 py-2 font-body text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground", values.restaurantPreference === "recommendation" && "border-bronze bg-primary-foreground/5 text-primary-foreground")}>{t("I’d like a recommendation", "Ik ontvang graag een aanbeveling")}</Button>
                      <Button type="button" variant="outline" onClick={() => update("restaurantPreference", "specific")} className={cn("h-auto min-h-12 whitespace-normal rounded-sm border-primary-foreground/10 bg-transparent px-3 py-2 font-body text-primary-foreground/60 hover:bg-primary-foreground/5 hover:text-primary-foreground", values.restaurantPreference === "specific" && "border-bronze bg-primary-foreground/5 text-primary-foreground")}>{t("I have a restaurant in mind", "Ik heb een restaurant in gedachten")}</Button>
                    </div>
                    {values.restaurantPreference === "specific" && <div className="mt-5"><Input value={values.preferredRestaurant} onChange={(event) => update("preferredRestaurant", event.target.value)} maxLength={100} placeholder={t("Restaurant name", "Naam restaurant")} className={inputClass} /><FieldError message={errors.preferredRestaurant} /></div>}
                  </div>
                </>
              )}
            </div>

            <div className="mt-12 flex items-center justify-between gap-4">
              <Button type="button" variant="ghost" onClick={() => setStep(1)} className="rounded-none px-0 font-label text-primary-foreground/45 hover:bg-transparent hover:text-primary-foreground"><ChevronLeft className="h-4 w-4" />{t("Back", "Terug")}</Button>
              <Button type="button" onClick={next} className="h-12 rounded-sm bg-primary-foreground px-6 font-label text-warm-black hover:bg-primary-foreground/90">{t("Contact details", "Contactgegevens")}<ChevronRight className="h-4 w-4" /></Button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="contact" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={stepTransition}>
            <span className="font-label text-bronze">{t("Contact details", "Contactgegevens")}</span>
            <h2 className="mt-4 font-display text-3xl leading-tight text-primary-foreground md:text-4xl">{t("How may we reach you?", "Hoe mogen wij u bereiken?")}</h2>
            <div className="mt-10 grid grid-cols-1 gap-x-8 gap-y-9 md:grid-cols-2">
              <div><Label htmlFor="full-name" className={labelClass}>{t("Full name", "Volledige naam")}</Label><Input id="full-name" value={values.fullName} onChange={(event) => update("fullName", event.target.value)} maxLength={100} autoComplete="name" className={inputClass} /><FieldError message={errors.fullName} /></div>
              <div><Label htmlFor="email" className={labelClass}>Email</Label><Input id="email" type="email" value={values.email} onChange={(event) => update("email", event.target.value)} maxLength={255} autoComplete="email" className={inputClass} /><FieldError message={errors.email} /></div>
              <div className="md:col-span-2"><Label htmlFor="phone" className={labelClass}>{t("Phone / WhatsApp", "Telefoon / WhatsApp")}</Label><Input id="phone" type="tel" value={values.phone} onChange={(event) => update("phone", event.target.value)} maxLength={40} autoComplete="tel" className={inputClass} /><FieldError message={errors.phone} /></div>
              <div className="md:col-span-2"><Label htmlFor="additional-requests" className={labelClass}>{t("Additional requests (optional)", "Aanvullende wensen (optioneel)")}</Label><Textarea id="additional-requests" value={values.additionalRequests} onChange={(event) => update("additionalRequests", event.target.value)} maxLength={2000} rows={4} placeholder={t("Anything else our concierge should know…", "Alles wat onze concierge verder mag weten…")} className={cn(inputClass, "min-h-28 resize-none py-3")} /></div>
            </div>
            <div className="mt-12 flex items-center justify-between gap-4">
              <Button type="button" variant="ghost" onClick={() => setStep(2)} className="rounded-none px-0 font-label text-primary-foreground/45 hover:bg-transparent hover:text-primary-foreground"><ChevronLeft className="h-4 w-4" />{t("Back", "Terug")}</Button>
              <Button type="button" onClick={submit} disabled={loading} className="h-12 rounded-sm bg-primary-foreground px-6 font-label text-warm-black hover:bg-primary-foreground/90">{loading ? t("Sending…", "Verzenden…") : t("Request Concierge Assistance", "Vraag Concierge Assistentie")}</Button>
            </div>
          </motion.div>
        )}

        {step === 4 && (
          <motion.div key="confirmation" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={stepTransition} className="flex min-h-[420px] flex-col items-center justify-center text-center">
            <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-full border border-bronze/50 text-bronze"><Check className="h-5 w-5" /></div>
            <span className="font-label text-bronze">{t("Request received", "Aanvraag ontvangen")}</span>
            <h2 className="mt-5 max-w-lg font-display text-3xl leading-tight text-primary-foreground md:text-4xl">{t("Thank you.", "Dank u.")}</h2>
            <p className="mt-5 max-w-lg font-body text-sm leading-7 text-primary-foreground/55 md:text-base">{t("Your request has been received. Our concierge team will review your preferences and contact you shortly.", "Uw aanvraag is ontvangen. Ons conciergeteam beoordeelt uw voorkeuren en neemt binnenkort contact met u op.")}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}